import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import jobsData from '../data/jobsData';
import { slugify } from '../utils/slugify';

const OrganizationDetails = () => {
  const { slug } = useParams();
  const [organization, setOrganization] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Define the mapping between organization slugs and actual company names from jobsData
  // These map the organization names on the Organizations page to real companies in jobsData

  useEffect(() => {
    // Define the mapping between organization slugs and actual company names from jobsData
    // These map the organization names on the Organizations page to real companies in jobsData
    const orgSlugToCompany = {
      'tech-solutions-llc': 'Tech Solutions LLC',      // Maps to first company in jobsData
      'digital-solutions': 'Digital Solutions',         // Maps to second company in jobsData
      'cloud-systems': 'Cloud Systems',                 // Maps to third company in jobsData
      'innovative-tech': 'Innovative Tech',             // Maps to fourth company in jobsData
      'global-systems-inc': 'Global Systems Inc',       // Maps to fifth company in jobsData
      'global-marketing-group': 'Global Marketing Group' // Maps to sixth company in jobsData
    };
    
    // Since the organization slugs on the Organizations page don't correspond to the 
    // actual company info in jobsData, I'll create a mapping system
    // or look for the company by slug if they match
    
    let foundOrg = null;
    
    // First, try to find by using the mapping to company name
    if (orgSlugToCompany[slug]) {
      const company = jobsData.find(job => job.company === orgSlugToCompany[slug]);
      if (company) {
        foundOrg = company.companyInfo;
      }
    }
    
    // If still not found, try to match by company name directly
    if (!foundOrg) {
      const company = jobsData.find(job => 
        slugify(job.company) === slug
      );
      if (company) {
        foundOrg = company.companyInfo;
      }
    }
    
    if (foundOrg) {
      setOrganization(foundOrg);
      // Get all jobs from this company
      const companyJobs = jobsData.filter(job => job.company === foundOrg.name);
      setJobs(companyJobs);
      document.title = `${foundOrg.name} | JobsDriver`;
    } else {
      // Handle case where organization is not found
      document.title = "Organization Not Found | JobsDriver";
    }
    
    setLoading(false);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organization details...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Organization Not Found</h1>
          <p className="text-gray-600 mb-8">The organization you're looking for doesn't exist or may have been removed.</p>
          <Link to="/organizations" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700">
            Browse All Organizations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <section className="relative py-24 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1920&q=80')" }}>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{organization.name}</h1>
          <div className="flex items-center space-x-2 text-white">
            <Link to="/" className="hover:text-primary-300 transition-colors">Home</Link>
            <i className="fas fa-chevron-right text-sm"></i>
            <Link to="/organizations" className="hover:text-primary-300 transition-colors">Organizations</Link>
            <i className="fas fa-chevron-right text-sm"></i>
            <span className="text-primary-300">{organization.name}</span>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Organization Header */}
              <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-200">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-shrink-0">
                    <img 
                      src="/logo.png" 
                      alt={`${organization.name} Logo`} 
                      className="w-24 h-24 rounded-xl object-contain bg-gray-100 p-2"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://placehold.co/96x96?text=${organization.name.charAt(0)}`;
                      }}
                    />
                  </div>
                  <div className="flex-grow">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{organization.name}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-map-marker-alt mr-3 text-primary-500"></i>
                        <span>{organization.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-briefcase mr-3 text-primary-500"></i>
                        <span>{organization.industry}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-users mr-3 text-primary-500"></i>
                        <span>{organization.size}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-phone-alt mr-3 text-primary-500"></i>
                        <span>{organization.phone}</span>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <a href={organization.website} target="_blank" rel="noreferrer" className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200">
                        <i className="fas fa-globe mr-2"></i> Visit Website
                      </a>
                      <a href={`mailto:${organization.email}`} className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200">
                        <i className="fas fa-envelope mr-2"></i> Contact HR
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* About Section */}
              <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">About {organization.name}</h3>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {organization.name} is a leading company in the {organization.industry} industry, 
                    located in {organization.location}. With {organization.size} of employees, we are 
                    committed to delivering exceptional services and creating valuable opportunities 
                    for our team members.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    At {organization.name}, we pride ourselves on our innovative approach, 
                    professional development opportunities, and collaborative work environment. 
                    Our team is dedicated to excellence and continuous improvement.
                  </p>
                </div>
              </div>

              {/* Jobs at this Organization */}
              {jobs.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Jobs at {organization.name}</h3>
                  <div className="space-y-6">
                    {jobs.map((job) => (
                      <div key={job.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">
                              <Link to={`/job-details/${slugify(job.title)}`} className="hover:text-primary-600 transition-colors">
                                {job.title}
                              </Link>
                            </h4>
                            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
                              <div className="flex items-center">
                                <i className="fas fa-map-marker-alt mr-2"></i>
                                {job.location}
                              </div>
                              <div className="flex items-center">
                                <i className="fas fa-money-bill-wave mr-2"></i>
                                {job.salary}
                              </div>
                              <div className="flex items-center">
                                <i className="fas fa-briefcase mr-2"></i>
                                {job.type}
                              </div>
                            </div>
                          </div>
                          <Link 
                            to={`/job-details/${slugify(job.title)}`} 
                            className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 text-sm font-medium rounded-lg hover:bg-primary-600 hover:text-white transition-colors duration-200 whitespace-nowrap"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Organization Info */}
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <div className="text-center mb-6">
                  <img 
                    src="/logo.png" 
                    alt={`${organization.name} Logo`} 
                    className="w-16 h-16 mx-auto rounded-lg object-contain bg-gray-100 p-1 mb-3"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/64x64?text=${organization.name.charAt(0)}`;
                    }}
                  />
                  <h3 className="text-xl font-bold text-gray-900">{organization.name}</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-globe text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Website</h4>
                      <a href={organization.website} target="_blank" rel="noreferrer" className="text-primary-600 hover:text-primary-700 transition-colors break-all">
                        {organization.website.replace("https://", "")}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-phone-alt text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                      <a href={`tel:${organization.phone}`} className="text-primary-600 hover:text-primary-700 transition-colors">
                        {organization.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-envelope text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <a href={`mailto:${organization.email}`} className="text-primary-600 hover:text-primary-700 transition-colors break-all">
                        {organization.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-map-marker-alt text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Location</h4>
                      <p className="text-gray-700">{organization.location}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-users text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Company Size</h4>
                      <p className="text-gray-700">{organization.size}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-industry text-primary-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Industry</h4>
                      <p className="text-gray-700">{organization.industry}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* How to Apply */}
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6">How to Apply</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Send Your CV</h4>
                      <p className="text-gray-700 text-sm">Email your updated CV to {organization.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Visit Website</h4>
                      <p className="text-gray-700 text-sm">Check the official website for additional application instructions</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 text-sm font-bold flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Check Job Postings</h4>
                      <p className="text-gray-700 text-sm">Browse through our available job postings on this page</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Organization Stats Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">{jobs.length}</div>
              <div className="text-primary-100">Jobs Available</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">4.5</div>
              <div className="text-primary-100">Avg Rating</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">5+</div>
              <div className="text-primary-100">Years Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">{organization.size.split('-')[1] ? parseInt(organization.size.split('-')[1].replace('+', '').replace(' employees', '')) : 500}+</div>
              <div className="text-primary-100">Employees</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default OrganizationDetails;